/* eslint-disable no-restricted-globals */
/* eslint-disable arrow-body-style */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DECIMAL_PLACES, ONE_HUNDRED_VALUE } from 'common/config/app';
import { HYPHEN_SYMBOL, NUMBER, PERCENT, PERCENTAGE } from 'common/constants/general';
import { STRING } from 'common/constants/gridType';
import { ENTER_DATA } from 'common/constants/inputs';
import { isExpression, toString } from 'utilities';
import { ValueEditorProps } from './ValueEditor.props';

/* Migrated from ValueEditor.styles.ts */
// ValueEditor component doesn't have any specific styles
// This file exists for consistency with the component structure
export const useValueEditorStyles = () => ({
  // No specific styles needed for this component
});

function parseValue(
  tmpValue: any,
  endsWithDec: (value: any) => boolean,
  gridType?: string,
  maximumFractionDigits = 10
): any {
  /* True if:
    - tmpValue is undefined/null/NaN,
    - the last character is a dot followed by zero or more numbers
  */
  let numValue = Number(toString(tmpValue).split(',').join(''));
  if (!toString(tmpValue).length || Number.isNaN(numValue) || endsWithDec(tmpValue)) {
    return tmpValue;
  }

  if (gridType === PERCENTAGE) {
    numValue *= ONE_HUNDRED_VALUE;
  }
  if (numValue !== 0 || 1 / numValue === -Infinity) {
    return numValue.toLocaleString('en-US', { maximumFractionDigits });
  }
  return numValue;
}

function parseDecimal(
  dbDecimalPlaces: number,
  tmpValue: any,
  endsWithDec: (value: any) => boolean,
  isWithDecimals: boolean,
  gridType?: string
): any {
  let decimalPlaces = isWithDecimals ? dbDecimalPlaces + 1 : dbDecimalPlaces - 2;
  decimalPlaces = decimalPlaces < 0 || isNaN(decimalPlaces) ? 0 : decimalPlaces;
  return parseValue(tmpValue, endsWithDec, gridType, decimalPlaces);
}

const ValueEditor: React.FC<ValueEditorProps> = ({ cell, onKeyDown, onChange, doubleClicked, setDoubleClicked }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const {
    gridType,
    format,
    value: cellValue,
    defaultValue,
    isCellCalculated,
    rowNumberPair,
    dbDecimalPlaces,
    extraAttr: cellExtraAttr,
    alias: cellAlias,
    key: cellKey,
  } = cell || {};
  const { style, maximumFractionDigitsWhenEditing, maximumFractionDigits, inputScale = 1 } = format || {};
  const isPercentCell = style === PERCENT;
  const isNumeric = [NUMBER, PERCENTAGE].includes(gridType!);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const endsWithDec = useCallback(
    (searchDecimalNumbers: boolean) => (inputValue: any) => {
      // Return false on enter
      if (cellValue === inputValue) {
        return false;
      }
      const stringValue = toString(inputValue);
      if (searchDecimalNumbers) {
        return /\.\d*$/.test(stringValue);
      }
      return /\.$/.test(stringValue);
    },
    [cellValue]
  );

  const getDisplayValue = useCallback(
    (valueParam: any, initialLoad = false) => {
      if (cell?.expr === '=' || toString(valueParam) === '=') {
        return '=';
      }
      const isPairCell = !isNaN(rowNumberPair!);
      const isPairCellCalculated = isCellCalculated;
      const isCalculatedCellWithPair = isPercentCell && isPairCell && isPairCellCalculated;
      const isExpr = isExpression(toString(valueParam));

      const tmpValue =
        (gridType === NUMBER || isCalculatedCellWithPair) && isExpr && Number(cellValue) !== 0 ? cellValue : valueParam;
      const editingValue =
        gridType === NUMBER && Number.isFinite(Number(tmpValue)) && (initialLoad || isExpr)
          ? Number(tmpValue) / inputScale
          : tmpValue;
      switch (gridType) {
        case NUMBER:
          return parseValue(editingValue, endsWithDec(true));
        case PERCENTAGE:
          if (initialLoad) {
            return parseDecimal(dbDecimalPlaces!, tmpValue, endsWithDec(false), isPercentCell, gridType);
          }
          return parseValue(tmpValue, endsWithDec(true));
        default:
          return tmpValue;
      }
    },
    [
      dbDecimalPlaces,
      isCellCalculated,
      rowNumberPair,
      cellValue,
      endsWithDec,
      gridType,
      inputScale,
      isPercentCell,
      cell?.expr,
    ]
  );

  const [displayValue, setDisplayValue] = useState(getDisplayValue(cellValue ?? '', true));
  const [wasDoubleClicked, setWasDoubleClicked] = useState(false);

  // The rule is that if we double click into the cell, we should preserve the original value,
  // other wise we should start typing on a blank slate
  useEffect(() => {
    if (doubleClicked) {
      setDoubleClicked?.(false);
      setWasDoubleClicked(true);
      // Auto-select all text so the user can immediately type to replace the value
      inputRef.current?.select();
    } else if (!doubleClicked && !wasDoubleClicked) {
      setDisplayValue('');
    }
  }, [doubleClicked, setDoubleClicked, wasDoubleClicked]);

  const inputId = useMemo(() => {
    if (cellExtraAttr) {
      return `${cellExtraAttr}-${cellAlias}-${cellKey}_editor`;
    }

    return `${cellAlias}-${cellKey}_editor`;
  }, [cellAlias, cellExtraAttr, cellKey]);

  const getPercentageValue = (currentValue: any): string => {
    const numberParts = currentValue.toString().split('.');
    const isStartingWithDot = numberParts[0] === '' && numberParts.length > 1;
    const integerPartLength = numberParts[0].length === 0 && numberParts.length > 1 ? 1 : numberParts[0].length;
    const remainingDecimalPlaces = DECIMAL_PLACES - integerPartLength;
    const numericCurrentValue = Number(currentValue);
    const calculatedValue = isStartingWithDot ? numericCurrentValue : numericCurrentValue / ONE_HUNDRED_VALUE;

    return calculatedValue.toFixed(remainingDecimalPlaces).toString();
  };

  const getTargetValue = (valueParam: any): any => {
    let targetValue;
    const isExpr = isExpression(valueParam.toString());

    if (isExpr) {
      targetValue = valueParam;
    } else {
      // if valueParam is not an expression, remove commas
      targetValue = valueParam.split(',').join('');

      // if valueParam includes a percentage sign, remove it
      if (targetValue.includes('%')) {
        targetValue = targetValue.replace('%', '');
      }

      // if valueParam is a number and gridType is PERCENTAGE, convert it to a percentage
      if (!Number.isNaN(Number(targetValue)) && gridType === PERCENTAGE && !endsWithDec(false)(targetValue)) {
        targetValue = getPercentageValue(targetValue);
      }

      // if valueParam is a hyphen and gridType is a number, set it to 0
      if (targetValue === HYPHEN_SYMBOL && isNumeric) {
        targetValue = '0';
      } else if (!isNumeric) {
        targetValue = valueParam;
      }
    }
    return targetValue;
  };

  const countCommasInString = (str: any, position: number): number => {
    // Count the number of commas in a string up to a given position
    return str?.length ? (toString(str).substring(0, position).match(/,/g) || []).length : 0;
  };

  const getCursorPosition = (value: any, position: number): number => {
    const formattedValue = getDisplayValue(value);
    // Count total of commas on the value returned on event.target.value;
    const commasInTargetValue = countCommasInString(value, position);
    // Count total of commas on the formatted value (format with commas separators);
    const commasInFormattedValue = countCommasInString(formattedValue, position);
    // Calculate the difference between the two values
    const totalCommasToConsider = commasInFormattedValue - commasInTargetValue;
    // Calculate the new position
    return position + totalCommasToConsider;
  };

  // basically, on every change we will set the display value, and also update true value
  const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const { target } = e;
    const { value, selectionStart: position } = target;
    setDisplayValue(getDisplayValue(value));
    const targetValue = getTargetValue(value);
    await onChange?.(targetValue);
    if (isNumeric) {
      const cursorPosition = getCursorPosition(value, position!);
      target.selectionEnd = cursorPosition;
    }
  };

  const handleFocus = () => {
    let sanitizedValue: any = cellValue ?? '';

    if (cell?.expr === '=') {
      setDisplayValue('=');
      return;
    }

    if (gridType) {
      switch (gridType) {
        case NUMBER:
          sanitizedValue = Number(cellValue);
          break;
        case PERCENTAGE:
          sanitizedValue = Number((cellValue as number) * ONE_HUNDRED_VALUE);
          break;
        case STRING:
          sanitizedValue = cellValue === ENTER_DATA ? '' : cellValue;
          break;
        default:
          sanitizedValue = Number(cellValue);
      }
    }

    if (toString(sanitizedValue) === toString(defaultValue) || toString(sanitizedValue) === '0') {
      onChange?.('');
    }
    const isPlaceHolder = toString(sanitizedValue) === 'ENTER NAME';
    const editingValue = gridType === NUMBER ? Number(sanitizedValue) / inputScale : sanitizedValue;
    const displayedValue =
      isPlaceHolder || sanitizedValue === null
        ? ''
        : editingValue.toLocaleString('en-US', {
          maximumFractionDigits: maximumFractionDigitsWhenEditing ?? maximumFractionDigits,
        });

    setDisplayValue(isPercentCell ? `${displayedValue}%` : displayedValue);
  };

  return (
    <input
      id={inputId}
      ref={inputRef}
      className="data-editor"
      value={displayValue}
      onChange={handleChange}
      onFocus={handleFocus}
      onKeyDown={onKeyDown}
    />
  );
};

export default ValueEditor;
